import { useCallback, type FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { useCalendarId } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';

import { useTrelloRemoveCardFromCalendarEventMutation } from './TrelloRemoveCardFromCalendarEventMutation.generated';
import { usePlannerAccount } from './usePlannerAccount';
import { useWorkspaceAri } from './useWorkspaceAri';

import * as styles from './UnlinkCardConfirmationContent.module.less';

interface UnlinkCardConfirmationContentProps {
  cardId: string;
  plannerEventCardId: string;
  onUnlinkSuccess?: () => void;
  source?: SourceType;
}

export const UnlinkCardConfirmationContent: FunctionComponent<
  UnlinkCardConfirmationContentProps
> = ({
  cardId,
  plannerEventCardId,
  onUnlinkSuccess,
  source = 'plannerScreen' as SourceType,
}) => {
  const intl = useIntl();
  const calendarId = useCalendarId();
  const { workspaceId } = useWorkspaceAri();
  const { plannerAccountId } = usePlannerAccount();
  const [removeCardFromCalendarEvent] =
    useTrelloRemoveCardFromCalendarEventMutation();

  const containers = formatContainers({ workspaceId });

  const handleUnlinkConfirm = useCallback(async () => {
    if (!plannerAccountId) {
      return;
    }

    const taskName = 'delete-plannerEventCard';
    const traceId = Analytics.startTask({ taskName, source });

    Analytics.sendClickedButtonEvent({
      buttonName: 'unlinkCardConfirmButton',
      source,
      attributes: {
        personalProductivity: 'planner',
        containerType: 'planner',
        cardId,
      },
      containers,
    });

    try {
      await removeCardFromCalendarEvent({
        variables: {
          plannerCalendarEventCardId: plannerEventCardId,
          plannerCalendarId: calendarId,
          providerAccountId: plannerAccountId,
        },
        context: {
          traceId,
        },
        // Apollo doesn't allow you to optimistically use cache.evict so we have to use modify to evict
        // https://github.com/apollographql/apollo-client/issues/10289
        update: (cache, { data }) => {
          if (data?.trello?.removeCardFromPlannerCalendarEvent?.success) {
            cache.modify({
              id: cache.identify({
                __typename: 'TrelloPlannerCalendarEventCard',
                id: plannerEventCardId,
              }),
              fields: (_, { DELETE }) => DELETE,
            });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          trello: {
            __typename: 'TrelloMutationApi',
            removeCardFromPlannerCalendarEvent: {
              errors: null,
              eventCard: {
                id: plannerEventCardId,
                __typename: 'TrelloPlannerCalendarEventCardDeleted',
              },
              success: true,
              __typename: 'TrelloRemoveCardFromPlannerCalendarEventPayload',
            },
          },
        },
      });

      Analytics.taskSucceeded({ taskName, source, traceId });
      onUnlinkSuccess?.();
    } catch (error) {
      showFlag({
        id: 'unlinkPlannerCard',
        title: intl.formatMessage({
          id: 'templates.planner.unlink-card-from-event-error',
          defaultMessage:
            'There was an error unlinking this card from this event, please try again',
          description: 'Error flag message for unlinking cards from an event',
        }),
        appearance: 'error',
        isAutoDismiss: true,
      });
      Analytics.taskFailed({ taskName, source, traceId, error });
      sendErrorEvent(error, {
        tags: { ownershipArea: 'trello-electric', feature: 'Planner' },
      });
    }
  }, [
    plannerAccountId,
    removeCardFromCalendarEvent,
    plannerEventCardId,
    calendarId,
    cardId,
    containers,
    intl,
    onUnlinkSuccess,
    source,
  ]);

  return (
    <>
      <div className={styles.unlinkMessage}>
        <FormattedMessage
          id="templates.planner.link-later"
          defaultMessage="You can link it again later"
          description="Message for unlinking card from popover"
        />
      </div>
      <Button
        onClick={handleUnlinkConfirm}
        appearance="danger"
        shouldFitContainer={true}
      >
        <FormattedMessage
          id="templates.planner.unlink"
          defaultMessage="Unlink"
          description="Unlink button"
        />
      </Button>
    </>
  );
};
